import { format } from "date-fns";

import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Spinner } from "@/components/Spinner/Spinner";

import { TrackerOrganization } from "../../schema/trackerOrganizations";
import { resolveSignalCellState } from "../../utils/signalScanState";

interface NoSignalCellProps {
	row: TrackerOrganization;
	/** Does the tracker's list have a signal config? Without one a search cannot run. */
	signalsConfigured: boolean;
	/** When the user asked for a scan of this company in this tab, if they did. */
	scanRequestedAt: Date | null;
	onRunSearch: () => void;
}

/**
 * The Signal cell of a company with no signal to show (PREC-3240): "Looking for signals" while a
 * scan is due or was asked for, "Not scanned yet" with a "Run signal search" button, or "No
 * relevant signals found · checked <date>" with "Run again" on hover. It used to read "No signal"
 * in both of the last two cases, so a company nobody had scanned looked like one with nothing to
 * find.
 */
export const NoSignalCell: FunctionComponent<NoSignalCellProps> = ({
	row,
	signalsConfigured,
	scanRequestedAt,
	onRunSearch,
}) => {
	const { t } = useTranslation();
	const tc = (key: string, options?: Record<string, string>) =>
		t(`profileTracker.signalColumn.${key}`, options);

	const state = resolveSignalCellState({
		signalsConfigured,
		signalLastCheckedAt: row.signalLastCheckedAt,
		createdAt: row.createdAt,
		scanRequestedAt,
	});

	if (state === "pending") {
		return (
			<div className="flex items-center gap-2 text-gray-400 text-sm italic">
				<Spinner color="#9ca3af" />
				<span>{tc("lookingForSignals")}</span>
			</div>
		);
	}

	const runButton = (label: string, className = "") =>
		signalsConfigured ? (
			<button
				className={`text-xs font-medium text-blue-action hover:underline whitespace-nowrap ${className}`}
				type="button"
				onClick={(event) => {
					event.stopPropagation();
					onRunSearch();
				}}
			>
				{label}
			</button>
		) : null;

	if (state === "notScanned") {
		return (
			<div className="flex items-center gap-2 text-sm">
				<span className="text-gray-400 italic whitespace-nowrap">{tc("notScanned")}</span>
				{runButton(tc("runSearch"))}
			</div>
		);
	}

	const checkedAt = row.signalLastCheckedAt ? new Date(row.signalLastCheckedAt) : null;

	return (
		<div className="group/nosignal flex items-center gap-2 text-sm">
			<span className="text-gray-400 italic whitespace-nowrap">
				{tc("noRelevantSignals")}
				{checkedAt && ` · ${tc("checkedOn", { date: format(checkedAt, "d MMM yyyy") })}`}
			</span>
			{runButton(tc("runAgain"), "hidden group-hover/nosignal:inline")}
		</div>
	);
};
